/**
 * useMlKitFaceDetection.ts
 *
 * Native ML Kit face-detection loop for Capacitor auth flows
 * (FaceAuthenticationModal — face-login / face-recovery).
 *
 * Emits the same FaceDetectionResult shape as useFacialRecognition so the
 * shared BiometricCaptureView / challenge executor / descriptor capture
 * pipeline works unchanged, but every signal comes from on-device ML Kit:
 *   - face bounding box                 → detection + distance guidance
 *   - headEulerAngleX/Y/Z               → pose (drives the 'turn' challenge)
 *   - left/rightEyeOpenProbability      → blink counter ('blink' challenge)
 *   - frame luminance (canvas)          → lighting guidance
 *
 * Why a separate hook: the WASM pipeline runs a continuous MediaPipe
 * FaceDetector + FaceLandmarker loop; the ML Kit Capacitor bridge only
 * accepts still images, so this polls (~4-6 fps) via a scratch canvas →
 * Filesystem cache file → processImage. For auth flows that's plenty —
 * the anti-replay guarantee comes from the server-issued action challenge,
 * not frame rate. The ML Kit classifier is far more reliable than the WASM
 * blendshape thresholds it replaces (real eye-open probabilities and real
 * Euler angles instead of tuned blendshape cutoffs).
 *
 * Offline-first attendance scanning stays on useFacialRecognition — ML Kit
 * is used for the ONLINE auth paths only.
 *
 * `isSupported` is false on web or when the plugin isn't registered — the
 * caller is expected to fall back to useFacialRecognition.
 */

import { useCallback, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  createInitialFaceDetection,
  FACIAL_RECOGNITION_CONFIG,
  type DistanceGuidance,
  type FaceBounds,
  type FaceDetectionResult,
  type LightingGuidance,
} from '@/hooks/useFacialRecognition';

// ==================== CONFIG ====================

/** Cache-dir filename the polling loop overwrites each frame. */
const FRAME_FILE = 'ta_mlkit_face_frame.jpg';
/** Width the video frame is downscaled to before analysis — keeps the
 *  JPEG encode, filesystem write, and native inference cheap. */
const ANALYSIS_WIDTH = 448;
const JPEG_QUALITY = 0.75;
/** Minimum delay between consecutive processImage calls. */
const POLL_FLOOR_MS = 120;
/** After this many consecutive native failures the engine is declared dead
 *  so the caller can fall back to the WASM pipeline. */
const MAX_CONSECUTIVE_ERRORS = 6;

// Blink state machine on ML Kit eye-open probabilities (0..1). A real blink
// drives the value well under 0.2; hysteresis avoids counting noise.
const EYE_CLOSED = 0.3;
const EYE_OPEN = 0.6;
/** Longer closure is a deliberate eye-close / face leaving frame, not a blink. */
const BLINK_MAX_CLOSED_MS = 900;

/** Bounds-center displacement (fraction of frame width) that latches the
 *  micro-movement liveness flag. */
const MOVE_LATCH_RATIO = 0.012;
/** Continuous presence required before readyToCapture. */
const PRESENCE_READY_MS = 700;
/** Pose envelope for a capture-ready frontal face. */
const FRONTAL_YAW_DEG = 20;
const FRONTAL_PITCH_DEG = 22;

// ==================== MODULE-LEVEL PLUGIN CACHE ====================

type FaceDetectionModule = typeof import('@capacitor-mlkit/face-detection');
type FilesystemModule = typeof import('@capacitor/filesystem');

let faceDetectionModulePromise: Promise<FaceDetectionModule> | null = null;
let filesystemModulePromise: Promise<FilesystemModule> | null = null;

const loadFaceDetection = () =>
  (faceDetectionModulePromise ??= import('@capacitor-mlkit/face-detection'));
const loadFilesystem = () =>
  (filesystemModulePromise ??= import('@capacitor/filesystem'));

// ==================== HOOK ====================

export interface UseMlKitFaceDetection {
  /** True on native platforms with the FaceDetection plugin registered. */
  isSupported: boolean;
  /** True once the first native processImage round-trip succeeded. */
  isInitialized: boolean;
  /** Flipped when the native path repeatedly fails — caller should fall
   *  back to the WASM engine instead of showing a dead camera. */
  nativeFailed: boolean;
  currentDetection: FaceDetectionResult | null;
  initializeCamera(video: HTMLVideoElement): Promise<void>;
  /** Parity with useFacialRecognition — ML Kit needs no model init step. */
  initializeDetector(): Promise<void>;
  /** Warm-up: one real processImage round-trip. Returns whether the native
   *  pipeline works end-to-end (i.e. blink classification is issuable). */
  enableLiveness(): Promise<boolean>;
  resetLiveness(): void;
  startDetection(scratchCanvas?: HTMLCanvasElement | null): void;
  stopDetection(): void;
  stopCamera(): void;
  openCameraSettings(): Promise<void>;
}

export function useMlKitFaceDetection(): UseMlKitFaceDetection {
  const [isSupported] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('FaceDetection');
    } catch {
      return false;
    }
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [nativeFailed, setNativeFailed] = useState(false);
  const [currentDetection, setCurrentDetection] = useState<FaceDetectionResult | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scratchCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lumaCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const runningRef = useRef(false);
  const pollTimerRef = useRef<number | null>(null);
  const consecutiveErrorsRef = useRef(0);
  /** file:// URI for FRAME_FILE — deterministic per directory, resolved once. */
  const frameUriRef = useRef<string | null>(null);

  // Liveness accumulators
  const blinkCountRef = useRef(0);
  const lastBlinkTimeRef = useRef(0);
  const eyesClosedAtRef = useRef(0);
  const microMovementRef = useRef(false);
  const prevCenterRef = useRef<{ x: number; y: number } | null>(null);
  const facePresentSinceRef = useRef<number | null>(null);
  const lastFrameAtRef = useRef(0);
  /** Authoritative last published detection — the poll loop reads this, not
   *  React state, to avoid stale closures between async frames. */
  const detectionRef = useRef<FaceDetectionResult | null>(null);

  // ── Camera ────────────────────────────────────────────────────────────
  const initializeCamera = useCallback(async (video: HTMLVideoElement) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    });
    streamRef.current = stream;
    videoRef.current = video;
    video.srcObject = stream;
    video.setAttribute('playsinline', 'true');
    video.muted = true;
    await video.play();
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => {
      try { track.stop(); } catch { /* noop */ }
    });
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    // Best-effort cleanup of the cache-dir frame file.
    void loadFilesystem()
      .then(({ Filesystem, Directory }) =>
        Filesystem.deleteFile({ path: FRAME_FILE, directory: Directory.Cache }))
      .catch(() => undefined);
  }, []);

  const initializeDetector = useCallback(async () => {
    // ML Kit's detector is created per processImage call — nothing to warm.
  }, []);

  // ── Frame transport ───────────────────────────────────────────────────
  /**
   * Draw the current video frame into the scratch canvas, return it as a
   * base64 JPEG plus the luminance measured on the same pixels.
   */
  const grabFrame = useCallback((): { base64: string; width: number; height: number; brightness: number } | null => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) return null;

    const scale = ANALYSIS_WIDTH / video.videoWidth;
    const w = ANALYSIS_WIDTH;
    const h = Math.round(video.videoHeight * scale);

    if (!scratchCanvasRef.current) {
      scratchCanvasRef.current = document.createElement('canvas');
    }
    const canvas = scratchCanvasRef.current;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);

    // Luminance on a 48px-wide thumbnail — cheap and identical to the WASM
    // hook's brightness metric (mean of r+g+b over sampled pixels).
    if (!lumaCanvasRef.current) {
      lumaCanvasRef.current = document.createElement('canvas');
    }
    const luma = lumaCanvasRef.current;
    const lw = 48;
    const lh = Math.max(1, Math.round(h * (lw / w)));
    luma.width = lw;
    luma.height = lh;
    const lctx = luma.getContext('2d', { willReadFrequently: true });
    let brightness = 128;
    if (lctx) {
      lctx.drawImage(canvas, 0, 0, lw, lh);
      const data = lctx.getImageData(0, 0, lw, lh).data;
      let total = 0;
      const count = data.length / 4;
      for (let i = 0; i < data.length; i += 4) {
        total += (data[i] + data[i + 1] + data[i + 2]) / 3;
      }
      brightness = count > 0 ? total / count : 128;
    }

    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    return { base64, width: w, height: h, brightness };
  }, []);

  const writeFrame = useCallback(async (base64: string): Promise<string> => {
    const { Filesystem, Directory } = await loadFilesystem();
    await Filesystem.writeFile({
      path: FRAME_FILE,
      data: base64,
      directory: Directory.Cache,
    });
    if (!frameUriRef.current) {
      const { uri } = await Filesystem.getUri({ path: FRAME_FILE, directory: Directory.Cache });
      frameUriRef.current = uri;
    }
    return frameUriRef.current;
  }, []);

  // ── Guidance bands (mirrors useFacialRecognition) ─────────────────────
  const distanceGuidance = useCallback((areaRatio: number): DistanceGuidance => {
    const optimalMin = FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MIN;
    const optimalMax = FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MAX;

    let status: DistanceGuidance['status'];
    let message: string;
    let distanceScore: number;

    if (areaRatio < optimalMin * 0.7) {
      status = 'too_far';
      message = 'Move much closer';
      distanceScore = areaRatio / optimalMin;
    } else if (areaRatio < optimalMin) {
      status = 'slightly_far';
      message = 'Move a bit closer';
      distanceScore = 0.8;
    } else if (areaRatio <= optimalMax) {
      status = 'perfect';
      message = 'Perfect distance!';
      distanceScore = 1.0;
    } else if (areaRatio <= optimalMax * 1.3) {
      status = 'slightly_close';
      message = 'Move back slightly';
      distanceScore = 0.8;
    } else {
      status = 'too_close';
      message = 'Move back';
      distanceScore = Math.max(0, 1 - (areaRatio - optimalMax) / 0.35);
    }

    return { status, message, faceAreaRatio: areaRatio, optimalMin, optimalMax, distanceScore };
  }, []);

  const lightingGuidance = useCallback((brightness: number): LightingGuidance => {
    const optimalMin = 80;
    const optimalMax = 180;

    let status: LightingGuidance['status'];
    let message: string;
    let lightingScore: number;

    if (brightness < optimalMin * 0.7) {
      status = 'too_dark';
      message = 'Add more light';
      lightingScore = brightness / optimalMin;
    } else if (brightness < optimalMin) {
      status = 'slightly_dark';
      message = 'Slightly dark';
      lightingScore = 0.8;
    } else if (brightness <= optimalMax) {
      status = 'good';
      message = 'Good lighting';
      lightingScore = 1.0;
    } else if (brightness <= optimalMax * 1.2) {
      status = 'slightly_bright';
      message = 'Slightly bright';
      lightingScore = 0.8;
    } else {
      status = 'too_bright';
      message = 'Too bright';
      lightingScore = Math.max(0, 1 - (brightness - optimalMax) / 100);
    }

    return {
      status, message, brightness,
      optimalMin, optimalMax, lightingScore,
      hasGlare: brightness > optimalMax * 1.2,
      hasShadows: brightness < optimalMin * 0.7,
    };
  }, []);

  // ── Frame result application ──────────────────────────────────────────
  const applyFaces = useCallback((
    faces: import('@capacitor-mlkit/face-detection').Face[],
    imgW: number,
    imgH: number,
    brightness: number,
  ) => {
    const now = Date.now();
    const dt = lastFrameAtRef.current > 0 ? now - lastFrameAtRef.current : 0;
    lastFrameAtRef.current = now;

    const prev = detectionRef.current;
    const video = videoRef.current;
    const videoW = video?.videoWidth || imgW;
    const videoH = video?.videoHeight || imgH;

    // Largest face wins when several are present.
    const face = faces.length > 0
      ? faces.reduce((a, b) =>
          (b.bounds.right - b.bounds.left) * (b.bounds.bottom - b.bounds.top)
          > (a.bounds.right - a.bounds.left) * (a.bounds.bottom - a.bounds.top) ? b : a)
      : null;

    const lighting = lightingGuidance(brightness);

    if (!face) {
      facePresentSinceRef.current = null;
      prevCenterRef.current = null;
      eyesClosedAtRef.current = 0;
      const empty = createInitialFaceDetection();
      empty.lighting = lighting;
      // Preserve session-latched liveness so a dropped frame doesn't reset progress.
      empty.liveness.blinkCount = blinkCountRef.current;
      empty.liveness.lastBlinkTime = lastBlinkTimeRef.current;
      empty.liveness.microMovementDetected = microMovementRef.current;
      detectionRef.current = empty;
      setCurrentDetection(empty);
      return;
    }

    // Scale ML Kit's image-space rect into video-pixel space (same unit the
    // WASM detector emits — extractFaceApiDescriptor expects it).
    const sx = videoW / imgW;
    const sy = videoH / imgH;
    const bw = (face.bounds.right - face.bounds.left) * sx;
    const bh = (face.bounds.bottom - face.bounds.top) * sy;
    const bounds: FaceBounds = {
      x: face.bounds.left * sx,
      y: face.bounds.top * sy,
      width: bw,
      height: bh,
      centerX: (face.bounds.left + (face.bounds.right - face.bounds.left) / 2) * sx,
      centerY: (face.bounds.top + (face.bounds.bottom - face.bounds.top) / 2) * sy,
    };

    // Presence accounting
    if (facePresentSinceRef.current === null) {
      facePresentSinceRef.current = now;
    }
    const presenceMs = now - facePresentSinceRef.current;

    // Micro-movement: normalized center displacement between frames, latched.
    const prevCenter = prevCenterRef.current;
    if (prevCenter) {
      const dx = (bounds.centerX - prevCenter.x) / videoW;
      const dy = (bounds.centerY - prevCenter.y) / videoH;
      if (Math.sqrt(dx * dx + dy * dy) >= MOVE_LATCH_RATIO) {
        microMovementRef.current = true;
      }
    }
    prevCenterRef.current = { x: bounds.centerX, y: bounds.centerY };

    // Blink machine on eye-open probabilities.
    const l = face.leftEyeOpenProbability;
    const r = face.rightEyeOpenProbability;
    let eyeOpen: number | null = null;
    if (l != null && r != null) eyeOpen = (l + r) / 2;
    else if (l != null) eyeOpen = l;
    else if (r != null) eyeOpen = r;

    if (eyeOpen != null) {
      if (eyeOpen <= EYE_CLOSED) {
        if (eyesClosedAtRef.current === 0) eyesClosedAtRef.current = now;
      } else if (eyeOpen >= EYE_OPEN && eyesClosedAtRef.current > 0) {
        const closedFor = now - eyesClosedAtRef.current;
        eyesClosedAtRef.current = 0;
        if (closedFor <= BLINK_MAX_CLOSED_MS) {
          blinkCountRef.current += 1;
          lastBlinkTimeRef.current = now;
          microMovementRef.current = true; // a blink is itself movement
        }
      }
    }

    const distance = distanceGuidance((bounds.width * bounds.height) / (videoW * videoH));
    const yaw = face.headEulerAngleY ?? 0;
    const pitch = face.headEulerAngleX ?? 0;
    const roll = face.headEulerAngleZ ?? 0;
    const frontal = Math.abs(yaw) <= FRONTAL_YAW_DEG && Math.abs(pitch) <= FRONTAL_PITCH_DEG;
    const eyesOk = eyeOpen === null || eyeOpen >= 0.5;

    const readyToCapture = Boolean(
      presenceMs >= PRESENCE_READY_MS
      && distance.status !== 'too_far' && distance.status !== 'too_close'
      && lighting.status !== 'too_dark' && lighting.status !== 'too_bright'
      && frontal
      && eyesOk
    );

    const poseScore = Math.max(0, 1 - (Math.abs(yaw) + Math.abs(pitch) + Math.abs(roll)) / 135);
    const prevLiveness = prev?.liveness;
    const next: FaceDetectionResult = {
      detected: true,
      bounds,
      confidence: 0.9, // ML Kit plugin exposes no detection score
      landmarks: [],
      metrics: {
        presence: Math.min(1, presenceMs / 1500),
        stability: prev?.metrics.stability ?? 0.5,
        quality: Math.min(1, (distance.distanceScore + lighting.lightingScore + poseScore) / 3),
        liveness: blinkCountRef.current > 0 ? 1 : microMovementRef.current ? 0.6 : Math.min(0.5, presenceMs / 6000),
        distance: distance.distanceScore,
        confidence: 0.9,
        pose: { yaw, pitch, roll, poseScore },
      },
      liveness: {
        blinkCount: blinkCountRef.current,
        lastBlinkTime: lastBlinkTimeRef.current,
        earHistory: prevLiveness?.earHistory ?? [],
        poseHistory: [
          ...(prevLiveness?.poseHistory ?? []).slice(-29),
          { yaw, pitch, roll, timestamp: now },
        ],
        microMovementDetected: microMovementRef.current,
        facePresenceMs: (prevLiveness?.facePresenceMs ?? 0) + Math.max(0, dt),
        // Honest engine flags: the WASM landmarker is not running on this path.
        landmarkerActive: false,
        facialDepthOk: false,
        faceDepthRatio: 0,
        nonRigidMotionDetected: false,
        deviceMotionDetected: false,
        deviceMotionLevel: 0,
      },
      distance,
      lighting,
      readyToCapture,
    };
    detectionRef.current = next;
    setCurrentDetection(next);
  }, [distanceGuidance, lightingGuidance]);

  // ── Poll loop ─────────────────────────────────────────────────────────
  /** Serializes frame processing — all callers share one pipeline since
   *  every call overwrites the same cache file. */
  const inflightRef = useRef<Promise<void> | null>(null);

  const processFrameOnce = useCallback(async () => {
    const frame = grabFrame();
    if (!frame) throw new Error('no frame');

    const uri = await writeFrame(frame.base64);
    const { FaceDetection, PerformanceMode, ClassificationMode } = await loadFaceDetection();
    const result = await FaceDetection.processImage({
      path: uri,
      performanceMode: PerformanceMode.Fast,
      classificationMode: ClassificationMode.All,
      minFaceSize: 0.12,
      enableTracking: false, // a fresh detector is created per call anyway
    });
    applyFaces(result.faces ?? [], frame.width, frame.height, frame.brightness);
  }, [applyFaces, grabFrame, writeFrame]);

  const processFrame = useCallback((): Promise<void> => {
    inflightRef.current ??= processFrameOnce().finally(() => {
      inflightRef.current = null;
    });
    return inflightRef.current;
  }, [processFrameOnce]);

  const tick = useCallback(() => {
    if (!runningRef.current) return;
    const t0 = performance.now();
    processFrame()
      .then(() => {
        consecutiveErrorsRef.current = 0;
        setIsInitialized(true);
      })
      .catch((error) => {
        consecutiveErrorsRef.current += 1;
        if (consecutiveErrorsRef.current === 1) {
          console.warn('[MlKitFace] Frame processing failed:', error);
        }
        if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
          console.error('[MlKitFace] Native path failed repeatedly — flagging for WASM fallback');
          setNativeFailed(true);
          runningRef.current = false;
          return;
        }
      })
      .finally(() => {
        if (!runningRef.current) return;
        const elapsed = performance.now() - t0;
        pollTimerRef.current = window.setTimeout(tick, Math.max(0, POLL_FLOOR_MS - elapsed));
      });
  }, [processFrame]);

  const startDetection = useCallback((_scratchCanvas?: HTMLCanvasElement | null) => {
    if (runningRef.current) return;
    runningRef.current = true;
    consecutiveErrorsRef.current = 0;
    tick();
  }, [tick]);

  const stopDetection = useCallback(() => {
    runningRef.current = false;
    lastFrameAtRef.current = 0;
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // ── Liveness lifecycle ────────────────────────────────────────────────
  /**
   * For the ML Kit path "liveness" is intrinsic — the classifier returns
   * eye-open probabilities on every frame. This call validates the native
   * pipeline end-to-end (write → processImage) so the modal knows blink
   * challenges are issuable before asking the server for a sequence.
   */
  const enableLiveness = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;
    try {
      await processFrame();
      setIsInitialized(true);
      return true;
    } catch (error) {
      console.warn('[MlKitFace] enableLiveness round-trip failed:', error);
      return false;
    }
  }, [isSupported, processFrame]);

  const resetLiveness = useCallback(() => {
    blinkCountRef.current = 0;
    lastBlinkTimeRef.current = 0;
    eyesClosedAtRef.current = 0;
    microMovementRef.current = false;
    facePresentSinceRef.current = null;
    prevCenterRef.current = null;
    if (detectionRef.current) {
      const reset = createInitialFaceDetection();
      reset.lighting = detectionRef.current.lighting;
      detectionRef.current = reset;
      setCurrentDetection(reset);
    }
  }, []);

  const openCameraSettings = useCallback(async (): Promise<void> => {
    try {
      const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });
    } catch (error) {
      console.error('[MlKitFace] Failed to open settings:', error);
    }
  }, []);

  return {
    isSupported,
    isInitialized,
    nativeFailed,
    currentDetection,
    initializeCamera,
    initializeDetector,
    enableLiveness,
    resetLiveness,
    startDetection,
    stopDetection,
    stopCamera,
    openCameraSettings,
  };
}
